import { useState, type FormEvent, type ReactElement } from 'react'
import {
  useWebsiteCatalog,
  useWebsiteContent,
  useWebsiteHistory,
  useWebsiteMutation,
} from '../../api/website'
import { api, ApiError, API_URL } from '../../lib/api'
import { Page, PageHeader } from '../../components/Layout'
import type { AdminWebsiteContent, WebsiteKind, WebsitePayload } from '../../types/api'
const kinds: WebsiteKind[] = [
  'page',
  'article',
  'category',
  'faq',
  'testimonial',
  'announcement',
  'settings',
]
const labels: Record<WebsiteKind, string> = {
  page: 'الصفحات',
  article: 'مقالات المدونة',
  category: 'تصنيفات المدونة',
  faq: 'الأسئلة الشائعة',
  testimonial: 'آراء الطلاب',
  announcement: 'الإعلانات',
  settings: 'بيانات الموقع',
}
const historyActions: Record<string, string> = {
  create: 'إنشاء مسودة', save: 'حفظ المسودة', publish: 'نشر', unpublish: 'إلغاء النشر',
}
function formatWebsiteDate(value: string): string {
  return new Intl.DateTimeFormat('ar-EG-u-nu-latn', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}
function websiteError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback
  if (error.status === 409) return error.message.includes('address')
    ? 'عنوان الرابط مستخدم بالفعل لهذا النوع من المحتوى. افتح المحتوى الموجود لتعديله أو اختر عنوانًا آخر.'
    : 'عدّل شخص آخر هذا المحتوى. أعد تحميل النسخة المحفوظة قبل إعادة المحاولة.'
  if (error.status === 403) return 'ليست لديك صلاحية تعديل محتوى الموقع. سجّل الدخول بحساب مسؤول.'
  if (error.status === 401) return 'انتهت جلسة الدخول. سجّل الدخول مرة أخرى.'
  if (error.status === 413) return 'حجم الصورة كبير جدًا. اختر صورة لا تتجاوز 5 ميجابايت.'
  if (error.status === 400) {
    if (/startsAt|endsAt|end date/.test(error.message)) return 'راجع تاريخي بداية العرض ونهايته. استخدم تاريخًا صحيحًا، وأضف المنطقة الزمنية عند إدخال وقت. يجب أن تكون النهاية بعد البداية.'
    if (/category/.test(error.message)) return 'انشر التصنيف أولًا أو أزله من المقال قبل النشر.'
    if (/featured/.test(error.message)) return 'أحد العناصر المميزة لم يعد متاحًا. أزله قبل النشر.'
    if (/image|Image/.test(error.message) && !/both|language/.test(error.message)) return 'تعذّر قراءة الصورة. اختر صورة JPG أو PNG أو WebP لا تتجاوز 5 ميجابايت.'
    if (/address|slug/.test(error.message)) return 'راجع عنوان الرابط. استخدم أحرفًا إنجليزية صغيرة وأرقامًا وشرطات، واختر عنوانًا مدعومًا للصفحات.'
    return 'راجع الحقول المطلوبة: العنوان والنص ووصف الصورة باللغتين، وتاريخ السريان للسياسات، وصحة الروابط.'
  }
  return fallback
}
const blank: WebsitePayload = {
  titleEn: '',
  titleAr: '',
  summaryEn: '',
  summaryAr: '',
  bodyEn: '',
  bodyAr: '',
}
export function WebsitePage(): ReactElement {
  const query = useWebsiteContent()
  const [filter, setFilter] = useState<WebsiteKind>('page')
  const [selected, setSelected] = useState<string | null>(null)
  const [epoch, setEpoch] = useState(0)
  return (
    <div lang="ar" dir="rtl" className="website-content-ar">
    <Page>
      <PageHeader
        title="محتوى الموقع"
        description="عدّل المسودة وعاينها باللغتين، ثم انشرها على موقع الطلاب."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setSelected('new')
              setEpoch((n) => n + 1)
            }}
          >
            محتوى جديد
          </button>
        }
      />
      <div className="mb-5 flex flex-wrap gap-2">
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`btn ${filter === kind ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setFilter(kind)
              setSelected(null)
            }}
          >
            {labels[kind]}
          </button>
        ))}
      </div>
      {query.isPending && <p role="status">جارٍ تحميل محتوى الموقع…</p>}
      {query.isError && (
        <div role="alert">
          <p>{websiteError(query.error, 'تعذّر تحميل محتوى الموقع. حاول مرة أخرى.')}</p>
          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={() => void query.refetch()}
          >
            حاول مرة أخرى
          </button>
        </div>
      )}
      {query.data && (
        <div className="grid gap-6 xl:grid-cols-[18rem_1fr]">
          <aside className="space-y-2">
            {!query.data.some((row) => row.kind === filter) && (
              <p className="rounded-lg border border-rule p-5 text-sm text-ink-2">
                لا يوجد محتوى في قسم «{labels[filter]}» بعد. أنشئ مسودة للبدء.
              </p>
            )}
            {query.data
              .filter((row) => row.kind === filter)
              .map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className={`block w-full rounded-lg border p-4 text-start ${selected === row.id ? 'border-accent' : 'border-rule'}`}
                  onClick={() => setSelected(row.id)}
                >
                  <strong className="block">{row.draft.titleAr || row.draft.titleEn || row.slug}</strong>
                  <span className="mt-1 block text-xs text-ink-2">
                    <bdi dir="ltr">{row.slug}</bdi> ·{' '}
                    {row.published
                      ? row.revision === row.publishedRevision
                        ? 'منشور'
                        : 'تعديلات غير منشورة'
                      : 'مسودة'}
                  </span>
                </button>
              ))}
          </aside>
          <div>
            {selected && (
              <Editor
                key={`${selected}-${epoch}`}
                initial={query.data.find((row) => row.id === selected)}
                initialKind={filter}
                categories={query.data.filter((row) => row.kind === 'category')}
                onCreated={(row) => {
                  setFilter(row.kind)
                  setSelected(row.id)
                }}
                onReload={async () => {
                  await query.refetch()
                  setEpoch((n) => n + 1)
                }}
              />
            )}
            {!selected && (
              <div className="rounded-lg border border-rule p-8 text-ink-2">
                اختر محتوى لتعديله أو أنشئ مسودة جديدة. عناوين الصفحات المتاحة: <bdi dir="ltr">home, about, how-it-works, contact, become-a-tutor, privacy, terms</bdi>. استخدم <bdi dir="ltr">site</bdi> لبيانات الموقع.
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
    </div>
  )
}
function Editor({
  initial,
  initialKind,
  categories,
  onCreated,
  onReload,
}: {
  initial?: AdminWebsiteContent
  initialKind: WebsiteKind
  categories: AdminWebsiteContent[]
  onCreated: (row: AdminWebsiteContent) => void
  onReload: () => Promise<void>
}) {
  const [current, setCurrent] = useState(initial)
  const [kind, setKind] = useState<WebsiteKind>(initial?.kind ?? initialKind)
  const [slug, setSlug] = useState(initial?.slug ?? (initialKind === 'settings' ? 'site' : ''))
  const [draft, setDraft] = useState<WebsitePayload>(initial?.draft ?? blank)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<'en' | 'ar'>('ar')
  const [uploading, setUploading] = useState(false)
  const mutation = useWebsiteMutation()
  const catalog = useWebsiteCatalog()
  const history = useWebsiteHistory(current?.id ?? '')
  const dirty = JSON.stringify(draft) !== JSON.stringify(current?.draft ?? blank)
  const missing =
    !draft.titleEn?.trim() ||
    !draft.titleAr?.trim() ||
    ((['article', 'faq', 'testimonial'].includes(kind) || (kind === 'page' && slug !== 'home')) &&
      (!draft.bodyEn?.trim() || !draft.bodyAr?.trim())) ||
    (Boolean(draft.imageUrl) && (!draft.imageAltEn?.trim() || !draft.imageAltAr?.trim())) ||
    (kind === 'page' && ['privacy', 'terms'].includes(slug) && !draft.effectiveDate)
  function text(key: keyof WebsitePayload, value: string): void {
    setDraft((previous) => {
      const next = { ...previous }
      if (value.trim()) Object.assign(next, { [key]: value })
      else delete next[key]
      return next
    })
  }
  async function act(action: 'create' | 'save' | 'publish' | 'unpublish'): Promise<void> {
    setError('')
    setMessage('')
    try {
      const row = await mutation.mutateAsync({
        action,
        id: current?.id,
        revision: current?.revision,
        kind,
        slug,
        content: draft,
      })
      setCurrent(row)
      setDraft(row.draft)
      setMessage(
        action === 'publish'
          ? 'تم نشر المحتوى على الموقع.'
          : action === 'unpublish'
            ? 'تم إلغاء نشر المحتوى من الموقع.'
            : 'تم حفظ المسودة. النسخة المنشورة لم تتغير.',
      )
      if (action === 'create') onCreated(row)
    } catch (err) {
      setError(websiteError(err, 'تعذّر حفظ التغييرات. تحقق من اتصالك وحاول مرة أخرى.'))
    }
  }
  async function upload(file: File | undefined): Promise<void> {
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setError('اختر صورة حجمها أقل من 5 ميجابايت.')
      return
    }
    setUploading(true)
    setError('')
    try {
      const body = new FormData()
      body.append('file', file)
      const result = await api.post<{ url: string }>('/admin/website/media', body)
      setDraft((d) => ({ ...d, imageUrl: result.url }))
    } catch (err) {
      setError(websiteError(err, 'تعذّر رفع الصورة. اختر صورة JPG أو PNG أو WebP وحاول مرة أخرى.'))
    } finally {
      setUploading(false)
    }
  }
  function field(key: keyof WebsitePayload, label: string, area = false): React.ReactNode {
    const value = draft[key]
    const direction = key.endsWith('Ar') ? 'rtl' : 'ltr'
    const language = key.endsWith('Ar') ? 'ar' : key.endsWith('En') ? 'en' : undefined
    return (
      <label className="block text-sm" key={key} htmlFor={`website-${key}`}>
        <span className="mb-1.5 block font-medium">{label}</span>
        {area ? (
          <textarea
            id={`website-${key}`}
            rows={key.startsWith('body') ? 8 : 3}
            className="input w-full"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => text(key, e.target.value)}
            dir={direction}
            lang={language}
          />
        ) : (
          <input
            id={`website-${key}`}
            className="input w-full"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => text(key, e.target.value)}
            dir={direction}
            lang={language}
          />
        )}
      </label>
    )
  }
  function refs(
    key: 'featuredSubjectIds' | 'featuredTutorIds' | 'featuredPlanIds',
    label: string,
    options: { id: string; label: string }[],
  ): React.ReactNode {
    return (
      <label className="block text-sm">
        <span className="mb-2 block font-medium">{label}</span>
        <select
          multiple
          className="input min-h-32 w-full"
          value={draft[key] ?? []}
          onChange={(e) =>
            setDraft((d) => ({ ...d, [key]: Array.from(e.target.selectedOptions, (o) => o.value) }))
          }
        >
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-ink-2">
          يمكنك اختيار 12 عنصرًا كحد أقصى. اضغط Ctrl أو Command لاختيار عدة عناصر. يُحافظ على ترتيبها الحالي.
        </span>
      </label>
    )
  }
  const title = preview === 'ar' ? draft.titleAr : draft.titleEn
  const body = preview === 'ar' ? draft.bodyAr : draft.bodyEn
  const occurrences = new Map<string, number>()
  const paragraphs = (body ?? '').split(/\n\s*\n/).filter(Boolean).map(text => {
    const count = (occurrences.get(text) ?? 0) + 1
    occurrences.set(text, count)
    return { key: `${count}:${text}`, text }
  })
  return (
    <div className="space-y-6">
      <form
        className="card space-y-5 p-5"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void act(current ? 'save' : 'create')
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{current ? 'تعديل المسودة' : 'مسودة جديدة'}</h2>
          {current && (
            <button type="button" className="btn btn-secondary" onClick={() => void onReload()}>
              إعادة تحميل النسخة المحفوظة
            </button>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            نوع المحتوى
            <select
              className="input mt-2 w-full"
              disabled={Boolean(current)}
              value={kind}
              onChange={(e) => {
                const value = e.target.value
                if (kinds.some((k) => k === value)) setKind(value as WebsiteKind)
              }}
            >
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {labels[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            عنوان الرابط
            <input
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              maxLength={100}
              className="input mt-2 w-full"
              disabled={Boolean(current)}
              dir="ltr"
              lang="en"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </label>
        </div>
        <p className="text-xs leading-relaxed text-ink-2">
          استخدم أحد عناوين الصفحات: <bdi dir="ltr">home, about, how-it-works, contact, become-a-tutor, privacy, terms</bdi>، و<bdi dir="ltr">site</bdi> لبيانات الموقع. لا يتغير عنوان الرابط بعد الإنشاء. افصل الفقرات بسطر فارغ؛ ويُعرض HTML كنص فقط.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('titleEn', kind === 'faq' ? 'السؤال بالإنجليزية' : 'العنوان بالإنجليزية')}
          {field('titleAr', kind === 'faq' ? 'السؤال بالعربية' : 'العنوان بالعربية')}
          {field('summaryEn', 'المقدمة بالإنجليزية', true)}
          {field('summaryAr', 'المقدمة بالعربية', true)}
          {field('bodyEn', kind === 'faq' ? 'الإجابة بالإنجليزية' : 'النص بالإنجليزية', true)}
          {field('bodyAr', kind === 'faq' ? 'الإجابة بالعربية' : 'النص بالعربية', true)}
        </div>
        {kind === 'article' && (
          <label className="block text-sm">
            التصنيف
            <select
              className="input mt-2 w-full"
              value={draft.categorySlug ?? ''}
              onChange={(e) => text('categorySlug', e.target.value)}
            >
              <option value="">بدون تصنيف</option>
              {categories.map((category) => (
                <option key={category.id} value={category.slug}>
                  {category.draft.titleAr || category.draft.titleEn || category.slug}
                  {category.published ? '' : ' (مسودة)'}
                </option>
              ))}
            </select>
          </label>
        )}
        <details>
          <summary className="cursor-pointer font-medium">الصورة وبيانات المشاركة</summary>
          <div className="mt-4 space-y-4">
            <label className="block text-sm">
              رفع صورة (JPG أو PNG أو WebP، حتى 5 ميجابايت)
              <input
                className="mt-2 block w-full"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={uploading}
                onChange={(e) => void upload(e.target.files?.[0])}
              />
            </label>
            <p className="text-xs text-ink-2">
              تكون الصور متاحة للعامة فور رفعها. لا ترفع مستندات خاصة.
            </p>
            {uploading && <p role="status">جارٍ رفع الصورة…</p>}
            {field('imageUrl', 'رابط الصورة (رابط HTTPS أو صورة مرفوعة)')}
            <div className="grid gap-4 sm:grid-cols-2">
              {field('imageAltEn', 'وصف الصورة بالإنجليزية')}
              {field('imageAltAr', 'وصف الصورة بالعربية')}
              {field('seoTitleEn', 'عنوان نتائج البحث بالإنجليزية')}
              {field('seoTitleAr', 'عنوان نتائج البحث بالعربية')}
              {field('seoDescriptionEn', 'وصف نتائج البحث بالإنجليزية', true)}
              {field('seoDescriptionAr', 'وصف نتائج البحث بالعربية', true)}
            </div>
          </div>
        </details>
        <details>
          <summary className="cursor-pointer font-medium">الروابط والترتيب والتواريخ</summary>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {field('linkUrl', 'وجهة الرابط (مسار داخل الموقع أو رابط HTTPS)')}
            {field('linkLabelEn', 'نص الرابط بالإنجليزية')}
            {field('linkLabelAr', 'نص الرابط بالعربية')}
            {field('effectiveDate', 'تاريخ السريان (YYYY-MM-DD)')}
            {kind === 'announcement' && (
              <>
                {field('startsAt', 'بداية العرض (تاريخ أو وقت مع المنطقة الزمنية، اختياري)')}
                {field('endsAt', 'نهاية العرض (تاريخ أو وقت مع المنطقة الزمنية، اختياري)')}
              </>
            )}
            <label className="block text-sm">
              ترتيب العرض
              <input
                type="number"
                min={0}
                max={10000}
                className="input mt-2 w-full"
                value={draft.displayOrder ?? 0}
                onChange={(e) => setDraft((d) => ({ ...d, displayOrder: Number(e.target.value) }))}
              />
            </label>
          </div>
        </details>
        {kind === 'settings' && (
          <div className="space-y-4">
            <h3 className="font-semibold">بيانات التواصل وروابط التواصل الاجتماعي</h3>
            {field('contactEmail', 'البريد الإلكتروني للدعم')}
            {field('contactPhone', 'هاتف الدعم')}
            <label className="block text-sm">
              روابط التواصل الاجتماعي (رابط HTTPS واحد في كل سطر)
              <textarea
                className="input mt-2 w-full"
                rows={4}
                dir="ltr"
                value={(draft.socialUrls ?? []).join('\n')}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    socialUrls: e.target.value.split('\n').filter(Boolean),
                  }))
                }
              />
            </label>
          </div>
        )}
        {kind === 'page' && slug === 'home' && (
          <div className="space-y-4">
            <h3 className="font-semibold">المحتوى المميز في الصفحة الرئيسية</h3>
            {catalog.isError && (
              <p role="alert">تعذّر تحميل المواد والمعلمين والخطط. أعد تحميل الصفحة للمحاولة مرة أخرى.</p>
            )}
            {catalog.data && (
              <>
                {refs(
                  'featuredSubjectIds',
                  'المواد',
                  catalog.data.subjects.map((s) => ({ id: s.id, label: s.nameAr || s.nameEn })),
                )}
                {refs(
                  'featuredTutorIds',
                  'المعلمون',
                  catalog.data.tutors.map((t) => ({ id: t.id, label: t.fullName })),
                )}
                {refs(
                  'featuredPlanIds',
                  'خطط التعلم',
                  catalog.data.plans.map((p) => ({ id: p.id, label: p.nameAr || p.nameEn })),
                )}
              </>
            )}
          </div>
        )}
        {missing && (
          <p role="status" className="rounded-lg border border-rule p-3 text-sm text-ink-2">
            أكمل العنوان والنص المطلوب باللغتين قبل النشر، وأضف وصف الصورة باللغتين عند استخدامها. تحتاج صفحات السياسات إلى تاريخ سريان.
          </p>
        )}
        {dirty && current && (
          <p className="text-sm text-ink-2">
            لديك تعديلات غير محفوظة. احفظ المسودة قبل النشر.
          </p>
        )}
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        {message && <p role="status">{message}</p>}
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={mutation.isPending || uploading}
          >
            حفظ المسودة
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!current || dirty || Boolean(missing) || mutation.isPending || uploading}
            onClick={() => void act('publish')}
          >
            نشر
          </button>
          {current?.published && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={mutation.isPending || dirty}
              onClick={() => void act('unpublish')}
            >
              إلغاء النشر
            </button>
          )}
        </div>
        {current && (
          <p className="break-words text-xs text-ink-2">
            الإصدار {current.revision}.{' '}
            {current.publishedAt
              ? `نُشر في ${formatWebsiteDate(current.publishedAt)} بواسطة ${current.publishedBy}.`
              : 'لم يُنشر بعد.'}
          </p>
        )}
      </form>
      <section className="card p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-semibold">معاينة المسودة {dirty ? '· تعديلات غير محفوظة' : ''}</h2>
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setPreview('en')}>
              الإنجليزية
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setPreview('ar')}>
              العربية
            </button>
          </div>
        </div>
        <article
          dir={preview === 'ar' ? 'rtl' : 'ltr'}
          lang={preview}
          className="max-w-3xl rounded-lg border border-rule p-6"
        >
          {draft.imageUrl && (
            <img
              className="mb-6 max-h-64 w-full rounded-lg object-contain"
              src={draft.imageUrl.startsWith('/') ? `${API_URL}${draft.imageUrl}` : draft.imageUrl}
              alt={(preview === 'ar' ? draft.imageAltAr : draft.imageAltEn) ?? ''}
            />
          )}
          <h3 className="text-2xl font-semibold">{title || '…'}</h3>
          <p className="mt-4 text-ink-2">{preview === 'ar' ? draft.summaryAr : draft.summaryEn}</p>
          {paragraphs.map(paragraph => (
            <p key={paragraph.key} className="mt-5 whitespace-pre-line leading-relaxed">
              {paragraph.text}
            </p>
          ))}
        </article>
        <p className="mt-4 text-xs text-ink-2">
          هذه معاينة للمحتوى. يُعرض المحتوى على موقع الطلاب وفق تصميم الصفحة.
        </p>
      </section>
      {current && (
        <details className="card p-5">
          <summary className="cursor-pointer font-medium">سجل التعديلات والنشر</summary>
          {history.isPending && <p role="status">جارٍ تحميل السجل…</p>}
          {history.isError && <p role="alert">تعذّر تحميل السجل. أعد تحميل النسخة المحفوظة للمحاولة مرة أخرى.</p>}
          <ul className="mt-4 space-y-3 text-xs">
            {history.data?.map((event) => (
              <li key={event.id} className="break-words">
                {formatWebsiteDate(event.createdAt)} · {historyActions[event.action] ?? 'تحديث'} · الإصدار{' '}
                {event.revision} · <bdi dir="ltr">{event.actorId}</bdi>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
